
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/vue/24/solid";
import axios, { CanceledError } from "axios";
import { format, parseISO, subDays } from "date-fns";
import { defineComponent, PropType } from "vue";
import LoadingSpinner from "@/components/loading_state/LoadingSpinner.vue";
import CameraRepository from "@/repositories/CameraRepository";
import logger from "@/services/logger";
import { GalleryPaginatedImg } from "@/types/Camera";

export default defineComponent({
  name: "ImagesComparison",
  props: {
    camera: {
      type: String,
    },
    initialBeforeDate: {
      type: String as PropType<string>,
      required: false,
      default: "",
    },
    initialAfterDate: {
      type: String as PropType<string>,
      required: false,
      default: "",
    },
  },
  components: { LoadingSpinner, ChevronLeftIcon, ChevronRightIcon },
  data() {
    return {
      showOverlay: true as boolean,
      default_img: require("../../assets/imgs/default-project-thumbnail.jpg"),
      beforeImg: {} as Record<string, string | null | Date> as GalleryPaginatedImg,
      afterImg: {} as Record<string, string | null | Date> as GalleryPaginatedImg,
      beforeTime: this.initialBeforeDate as string,
      afterTime: this.initialAfterDate as string,
      loadingImage: false as boolean,
      beforeDateError: false as boolean,
      afterDateError: false as boolean,
      isFirstLoad: true as boolean,
      noData: false as boolean,
      beforeDateAbortController: null as AbortController | null,
      afterDateAbortController: null as AbortController | null,
    };
  },
  mounted() {
    this.initImg();
  },
  unmounted() {
    if (this.beforeDateAbortController) {
      this.beforeDateAbortController.abort();
    }
    if (this.afterDateAbortController) {
      this.afterDateAbortController.abort();
    }
  },
  emits: ["beforeChanged", "afterChanged", "noData"],
  watch: {
    beforeImg() {
      this.$emit("beforeChanged", this.beforeTime);
    },
    afterImg() {
      this.$emit("afterChanged", this.afterTime);
    },
    initialBeforeDate: {
      handler: function (value: string) {
        this.beforeTime = value;
        this.getCameraImages("after_sunrise", value);
      },
      immediate: true,
    },
    initialAfterDate: {
      handler: function (value: string) {
        this.afterTime = value;
        this.getCameraImages("before_sunset", value);
      },
      immediate: true,
    },
  },
  computed: {
    maxDate() {
      return format(new Date(), "yyyy-MM-dd");
    },
  },
  methods: {
    format,
    async getCameraImages(timeFilter: string, date: string) {
      const { customer_name, site_id } = this.$route.params;
      this.loadingImage = true;
      let signal: AbortSignal;
      if (timeFilter === "after_sunrise") {
        if (this.beforeDateAbortController) {
          this.beforeDateAbortController.abort();
        }
        this.beforeDateAbortController = new AbortController();
        signal = this.beforeDateAbortController.signal;
      } else {
        if (this.afterDateAbortController) {
          this.afterDateAbortController.abort();
        }
        this.afterDateAbortController = new AbortController();
        signal = this.afterDateAbortController.signal;
      }

      try {
        const img: GalleryPaginatedImg = await CameraRepository.loadGalleryPaginatedImg(
          customer_name as string,
          site_id as string,
          this.camera as string,
          date as string,
          timeFilter as string,
          { signal: signal },
        );

        if (timeFilter === "after_sunrise") {
          if (!img.url) {
            this.beforeDateError = true;
            if (!this.isFirstLoad) {
              this.showToast(
                "error",
                this.$t("camera.compare.no_image") + " " + format(parseISO(date), "dd.MM.yyyy"),
              );
            }
          }
          this.beforeImg = img ? img : this.default_img;
        } else {
          if (!img.url) {
            this.afterDateError = true;
            if (!this.isFirstLoad) {
              this.showToast(
                "error",
                this.$t("camera.compare.no_image") + " " + format(parseISO(date), "dd.MM.yyyy"),
              );
            }
          }
          if (img) {
            this.afterImg = img;
            if (!this.initialAfterDate && !this.afterTime) {
              this.afterTime = format(parseISO(this.afterImg.timestamp), "yyyy-MM-dd"); // Use "MM" for months and "dd" for days
              const date = parseISO(this.afterImg.timestamp);
              const newDate = subDays(date, 7);
              this.beforeTime = format(newDate, "yyyy-MM-dd"); // Use "MM" for months and "dd" for days
              await this.getCameraImages("after_sunrise", this.beforeTime);
            }
          } else {
            this.afterImg = this.default_img;
          }
        }
      } catch (error) {
        if (error instanceof CanceledError) {
          return;
        }

        logger.error(error);

        if (axios.isAxiosError(error)) {
          if (error?.response?.data?.code === "NO_DATA_YET") {
            this.$emit("noData");
          } else {
            this.showToast("warning", this.$t("err.no_data_found"));
          }
        }
        this.loadingImage = false;
      } finally {
        this.loadingImage = false;
        this.isFirstLoad = false;
      }
    },
    initImg() {
      const container = document.querySelector(`.container${this.camera}`) as HTMLElement | null;
      const slider = document.querySelector(`.slider${this.camera}`) as HTMLInputElement | null;

      if (container && slider) {
        slider.addEventListener(
          "input",
          (e) => {
            const target = e.target as HTMLInputElement;
            container.style.setProperty("--position", `${target.value}%`);
            this.showOverlay = false;
          },
          { passive: true },
        ); // Mark the event listener as passive
      }
    },
    prevBeforeImg() {
      this.beforeDateError = false;
      if (this.beforeImg.previous) {
        try {
          this.getCameraImages("after_sunrise", this.beforeImg.previous);
        } finally {
          this.beforeTime = this.beforeImg.previous;
        }
      }
    },
    nextBeforeImg() {
      this.beforeDateError = false;
      if (this.beforeImg.next) {
        try {
          this.getCameraImages("after_sunrise", this.beforeImg.next);
        } finally {
          this.beforeTime = this.beforeImg.next;
        }
      }
    },
    prevAfterImg() {
      this.afterDateError = false;
      if (this.afterImg.previous) {
        try {
          this.getCameraImages("before_sunset", this.afterImg.previous);
        } finally {
          this.afterTime = this.afterImg.previous;
        }
      }
    },
    nextAfterImg() {
      this.afterDateError = false;
      if (this.afterImg.next) {
        try {
          this.getCameraImages("before_sunset", this.afterImg.next);
        } finally {
          this.afterTime = this.afterImg.next;
        }
      }
    },
    changeBeforeTime(value: string) {
      this.beforeDateError = false;
      this.getCameraImages("after_sunrise", value);
    },
    changeAfterTime(value: string) {
      this.afterDateError = false;
      this.getCameraImages("before_sunset", value);
    },
  },
});
